import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCategories,
  getFilter,
  getFilteredEvents,
  getPresenterFilter,
  getPresenters,
  getSortOrder,
} from '../app/selectors';
import { AppActions } from '../app/slice';

export const useGalleryFilters = () => {
  const dispatch = useDispatch();
  const categories = useSelector(getCategories);
  const presenters = useSelector(getPresenters);
  const filter = useSelector(getFilter);
  const presenterFilter = useSelector(getPresenterFilter);
  const sortOrder = useSelector(getSortOrder);
  const filteredEvents = useSelector(getFilteredEvents);

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || undefined;
    dispatch(AppActions.filterChanged(value));
  };

  const handlePresenterFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || undefined;
    dispatch(AppActions.presenterFilterChanged(value));
  };

  const handleSortOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'newest' | 'oldest';
    dispatch(AppActions.sortOrderChanged(value));
  };

  const sortedEvents = React.useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      if (sortOrder === 'newest') {
        return Number(a.id) > Number(b.id) ? -1 : 1;
      } else {
        return Number(a.id) > Number(b.id) ? 1 : -1;
      }
    });
  }, [filteredEvents, sortOrder]);

  return {
    categories,
    presenters,
    filter,
    presenterFilter,
    sortOrder,
    sortedEvents,
    handleFilterChange,
    handlePresenterFilterChange,
    handleSortOrderChange,
  };
};

const GalleryFilters: React.FC = () => {
  const {
    categories,
    presenters,
    filter,
    presenterFilter,
    sortOrder,
    handleFilterChange,
    handlePresenterFilterChange,
    handleSortOrderChange,
  } = useGalleryFilters();

  return (
    <div className="gallery-filters">
      <div className="gallery-filters__left">
        <div className="gallery-filter">
          <select
            id="category-select"
            data-testid="category-select"
            className="gallery-filter__select"
            onChange={handleFilterChange}
            value={filter ?? ''}
            aria-label="Nach Kategorie filtern"
          >
            <option value="">Alle Kategorien</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div className="gallery-filter">
          <select
            id="presenter-select"
            className="gallery-filter__select"
            value={presenterFilter ?? ''}
            onChange={handlePresenterFilterChange}
            aria-label="Nach Referent filtern"
          >
            <option value="">Alle Referenten</option>
            {presenters.map((presenter) => (
              <option key={presenter} value={presenter}>
                {presenter}
              </option>
            ))}
          </select>
        </div>
        <div className="gallery-filter">
          <select
            id="sort-select"
            className="gallery-filter__select"
            value={sortOrder}
            onChange={handleSortOrderChange}
            aria-label="Sortierreihenfolge"
          >
            <option value="newest">Neueste zuerst</option>
            <option value="oldest">Älteste zuerst</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default GalleryFilters;
